import { SideEffectFunction } from '../../sideEffects';
import { CORE_API_HOSTNAME } from '../../env';
import fulfill from '../../fulfill';

import { Customer } from '../types';
import { ActionTypes, FetchCustomersResponse, FetchCustomersTotalCountResponse, FetchNextResponse, GetCustomers, GetCustomersReason } from './actions';
import { createURLSearchParams } from '../../helpers/urlParams';

const fetchNext: SideEffectFunction<FetchCustomersResponse, FetchNextResponse | undefined> = async (action) => {
    if (action.meta.cause.payload.reason === GetCustomersReason.refresh) {
        return;
    }

    const hasLimitInParams = action.meta.cause.payload.params.get('limit');
    const isAtLastPage = hasLimitInParams
        ? action.payload.customers.length < parseInt(hasLimitInParams)
        : action.payload.customers.length === 0;

    if (isAtLastPage) {
        return {
            type: ActionTypes.FetchNextResponse,
            payload: {
                customers: [],
            },
            meta: {
                cause: action,
            },
        };
    }

    const params = action.meta.cause.payload.params;
    const lastCustomer = action.payload.customers[action.payload.customers.length - 1];
    params.delete('starting_after');
    if (lastCustomer && lastCustomer.id) {
        params.append('starting_after', lastCustomer.customer_id);
    }
    params.delete('limit');
    params.append('limit', '1');

    const result: FetchNextResponse = await fulfill.get({
        accountId: action.meta.cause.payload.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${
            action.meta.cause.payload.accountId
        }/customers/users?${params.toString()}`,
        handlers: {
            200: (customers: Customer[]) => ({
                type: ActionTypes.FetchNextResponse,
                payload: {
                    customers: customers,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchCustomers: SideEffectFunction<GetCustomers, FetchCustomersResponse> = async (action) => {
    const { accountId, params } = action.payload;
    const result: FetchCustomersResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users?${params.toString()}`,
        handlers: {
            200: (customers: Customer[]) => ({
                type: ActionTypes.FetchCustomersResponse,
                payload: {
                    customers: customers,
                },
                meta: {
                    cause: action,
                },
            }),
            400: () => ({
                type: ActionTypes.FetchCustomersResponse,
                payload: {
                    customers: [],
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};
const fetchCustomersCount: SideEffectFunction<GetCustomers, FetchCustomersTotalCountResponse> = async (action) => {
    const { accountId } = action.payload;
    const params = createURLSearchParams({ limit: '1', 'total': 'true', type: 'any' });
    const result: FetchCustomersTotalCountResponse = await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/customers/users?${params.toString()}`,
        handlers: {
            200: (customers: Customer[], headers: Headers) => {
                return {
                    type: ActionTypes.FetchTotalCountResponse,
                    payload: {
                        totalCount: headers.get('total-count'),
                    },
                    meta: {
                        cause: action,
                    },
                };
            },
            400: () => ({
                type: ActionTypes.FetchTotalCountResponse,
                payload: {
                    totalCount: 0,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const effects = {
    [ActionTypes.GetCustomers]: fetchCustomers,
    [ActionTypes.FetchCustomersResponse]: fetchNext,
    [ActionTypes.GetCustomersCount]: fetchCustomersCount,
};

export default effects;
